import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Paper } from "@mui/material";
import { useChild } from "../../context/ChildContext";
import { useTranslation } from "react-i18next";

// WHO child growth standards — boys 0–24 months
const WHO_BOYS = [
  { Month: 0,  P3: 2.5, P15: 2.9, P50: 3.3, P85: 3.9, P97: 4.3 },
  { Month: 1,  P3: 3.4, P15: 3.9, P50: 4.5, P85: 5.1, P97: 5.7 },
  { Month: 2,  P3: 4.4, P15: 4.9, P50: 5.6, P85: 6.3, P97: 7.0 },
  { Month: 3,  P3: 5.1, P15: 5.6, P50: 6.4, P85: 7.2, P97: 7.9 },
  { Month: 4,  P3: 5.6, P15: 6.2, P50: 7.0, P85: 7.9, P97: 8.6 },
  { Month: 5,  P3: 6.1, P15: 6.7, P50: 7.5, P85: 8.4, P97: 9.2 },
  { Month: 6,  P3: 6.4, P15: 7.1, P50: 7.9, P85: 8.9, P97: 9.7 },
  { Month: 7,  P3: 6.7, P15: 7.4, P50: 8.3, P85: 9.3, P97: 10.2 },
  { Month: 8,  P3: 7.0, P15: 7.7, P50: 8.6, P85: 9.6, P97: 10.5 },
  { Month: 9,  P3: 7.2, P15: 7.9, P50: 8.9, P85: 10.0, P97: 10.9 },
  { Month: 10, P3: 7.5, P15: 8.2, P50: 9.2, P85: 10.3, P97: 11.2 },
  { Month: 11, P3: 7.7, P15: 8.4, P50: 9.4, P85: 10.5, P97: 11.5 },
  { Month: 12, P3: 7.8, P15: 8.6, P50: 9.6, P85: 10.8, P97: 11.8 },
  { Month: 13, P3: 8.0, P15: 8.8, P50: 9.9, P85: 11.1, P97: 12.1 },
  { Month: 14, P3: 8.2, P15: 9.0, P50: 10.1, P85: 11.3, P97: 12.4 },
  { Month: 15, P3: 8.4, P15: 9.2, P50: 10.3, P85: 11.6, P97: 12.7 },
  { Month: 16, P3: 8.5, P15: 9.4, P50: 10.5, P85: 11.8, P97: 12.9 },
  { Month: 17, P3: 8.7, P15: 9.6, P50: 10.7, P85: 12.0, P97: 13.2 },
  { Month: 18, P3: 8.9, P15: 9.7, P50: 10.9, P85: 12.3, P97: 13.5 },
  { Month: 19, P3: 9.0, P15: 9.9, P50: 11.1, P85: 12.5, P97: 13.7 },
  { Month: 20, P3: 9.2, P15: 10.1, P50: 11.3, P85: 12.7, P97: 14.0 },
  { Month: 21, P3: 9.3, P15: 10.3, P50: 11.5, P85: 13.0, P97: 14.3 },
  { Month: 22, P3: 9.5, P15: 10.5, P50: 11.8, P85: 13.2, P97: 14.5 },
  { Month: 23, P3: 9.7, P15: 10.6, P50: 12.0, P85: 13.4, P97: 14.8 },
  { Month: 24, P3: 9.8, P15: 10.8, P50: 12.2, P85: 13.7, P97: 15.1 },
];

// WHO child growth standards — girls 0–24 months
const WHO_GIRLS = [
  { Month: 0,  P3: 2.4, P15: 2.8, P50: 3.2, P85: 3.7, P97: 4.2 },
  { Month: 1,  P3: 3.2, P15: 3.6, P50: 4.2, P85: 4.8, P97: 5.4 },
  { Month: 2,  P3: 4.0, P15: 4.5, P50: 5.1, P85: 5.9, P97: 6.5 },
  { Month: 3,  P3: 4.6, P15: 5.1, P50: 5.8, P85: 6.7, P97: 7.4 },
  { Month: 4,  P3: 5.1, P15: 5.6, P50: 6.4, P85: 7.3, P97: 8.1 },
  { Month: 5,  P3: 5.5, P15: 6.1, P50: 6.9, P85: 7.8, P97: 8.7 },
  { Month: 6,  P3: 5.8, P15: 6.4, P50: 7.3, P85: 8.3, P97: 9.2 },
  { Month: 7,  P3: 6.1, P15: 6.7, P50: 7.6, P85: 8.7, P97: 9.6 },
  { Month: 8,  P3: 6.3, P15: 7.0, P50: 7.9, P85: 9.0, P97: 10.0 },
  { Month: 9,  P3: 6.6, P15: 7.3, P50: 8.2, P85: 9.3, P97: 10.4 },
  { Month: 10, P3: 6.8, P15: 7.5, P50: 8.5, P85: 9.6, P97: 10.7 },
  { Month: 11, P3: 7.0, P15: 7.7, P50: 8.7, P85: 9.9, P97: 11.0 },
  { Month: 12, P3: 7.1, P15: 7.9, P50: 8.9, P85: 10.2, P97: 11.3 },
  { Month: 13, P3: 7.3, P15: 8.1, P50: 9.2, P85: 10.4, P97: 11.6 },
  { Month: 14, P3: 7.5, P15: 8.3, P50: 9.4, P85: 10.7, P97: 11.9 },
  { Month: 15, P3: 7.7, P15: 8.5, P50: 9.6, P85: 10.9, P97: 12.2 },
  { Month: 16, P3: 7.8, P15: 8.7, P50: 9.8, P85: 11.2, P97: 12.5 },
  { Month: 17, P3: 8.0, P15: 8.8, P50: 10.0, P85: 11.4, P97: 12.7 },
  { Month: 18, P3: 8.2, P15: 9.0, P50: 10.2, P85: 11.6, P97: 13.0 },
  { Month: 19, P3: 8.3, P15: 9.2, P50: 10.4, P85: 11.9, P97: 13.3 },
  { Month: 20, P3: 8.5, P15: 9.4, P50: 10.6, P85: 12.1, P97: 13.5 },
  { Month: 21, P3: 8.7, P15: 9.6, P50: 10.9, P85: 12.4, P97: 13.8 },
  { Month: 22, P3: 8.8, P15: 9.8, P50: 11.1, P85: 12.6, P97: 14.1 },
  { Month: 23, P3: 9.0, P15: 9.9, P50: 11.3, P85: 12.8, P97: 14.3 },
  { Month: 24, P3: 9.2, P15: 10.1, P50: 11.5, P85: 13.1, P97: 14.6 },
];

const GrowthChart = ({ weightLog }) => {
  const { currentChild } = useChild();
  const { t } = useTranslation();

  const data = useMemo(() => {
    if (!currentChild?.birthDate || !weightLog?.length) return [];

    const percentiles = currentChild.gender === "girl" ? WHO_GIRLS : WHO_BOYS;
    const birthDate = new Date(currentChild.birthDate);

    // For each weight entry, compute age in whole months and keep the first per month
    const byMonth = {};
    for (const entry of weightLog) {
      const ageMs = new Date(entry.start) - birthDate;
      if (ageMs < 0) continue;
      const ageMonths = Math.floor(ageMs / (1000 * 60 * 60 * 24 * 30.4375));
      if (!byMonth[ageMonths] || entry.start < byMonth[ageMonths].start) {
        byMonth[ageMonths] = entry;
      }
    }

    if (!Object.keys(byMonth).length) return [];

    const maxMonth = Math.min(
      Math.max(...Object.keys(byMonth).map(Number)) + 1,
      percentiles.length - 1
    );

    return percentiles.slice(0, maxMonth + 1).map((row) => ({
      ...row,
      weight: byMonth[row.Month]
        ? +(byMonth[row.Month].weight / 1000).toFixed(3)
        : undefined,
    }));
  }, [currentChild, weightLog]);

  if (!data.some((d) => d.weight !== undefined)) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        bgcolor: "background.card",
        padding: "30px",
        paddingLeft: 0,
        borderRadius: 2,
      }}
    >
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="category"
            dataKey="Month"
            label={{
              value: t("charts.month"),
              position: "insideBottomRight",
              offset: -5,
            }}
          />
          <YAxis
            type="number"
            label={{
              value: "Kg",
              position: "insideBottomLeft",
              offset: 10,
            }}
            tickCount={20}
            tickFormatter={(tick) => tick.toFixed(1)}
            domain={["dataMin", "dataMax"]}
          />
          <Tooltip />
          <Line type="monotone" dataKey="P3" stroke="#ff0000" dot={false} />
          <Line type="monotone" dataKey="P15" stroke="#ffcc00" dot={false} />
          <Line type="monotone" dataKey="P50" stroke="#00cc00" dot={false} />
          <Line type="monotone" dataKey="P85" stroke="#ffcc00" dot={false} />
          <Line type="monotone" dataKey="P97" stroke="#ff0000" dot={false} />
          <Line
            type="monotone"
            dataKey="weight"
            stroke="#0000ff"
            strokeWidth={2}
            dot={{ r: 6 }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default GrowthChart;
